import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Phone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { HansaLogo } from "../components/HansaLogo";

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-5">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 border border-[#E5E0D8] shadow-sm cd-fade-up">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="login-logo">
            <HansaLogo size={44} />
          </Link>
          <LanguageSwitcher compact />
        </div>
        <h1 className="cd-heading text-3xl font-extrabold">{t("auth.welcomeBack")}</h1>
        <p className="text-[#52796F] mt-1 text-sm">{t("auth.welcomeBack.sub")}</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#1C1C1C]">{t("auth.email")}</label>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332]"
              placeholder="farmer@example.com"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#1C1C1C]">{t("auth.password")}</label>
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332]"
              placeholder="••••••••"
            />
          </div>
          {err && (
            <div data-testid="login-error" className="text-sm text-[#D90429] bg-[#D90429]/10 rounded-xl px-3 py-2">
              {err}
            </div>
          )}
          <button
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full bg-[#1B4332] text-white hover:bg-[#2D6A4F] py-3.5 rounded-full font-bold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? t("auth.signingIn") : t("common.login")}
          </button>
        </form>

        <Link
          to="/login"
          data-testid="login-to-phone"
          className="mt-3 w-full bg-white text-[#1B4332] border-2 border-[#1B4332] hover:bg-[#F4F1EA] py-3 rounded-full font-bold flex items-center justify-center gap-2"
        >
          <Phone className="w-4 h-4" /> {t("auth.useMobile")}
        </Link>

        <p className="text-sm text-center text-[#2D3A35] mt-6">
          {t("auth.newHere")}{" "}
          <Link data-testid="login-to-signup" to="/signup" className="text-[#1B4332] font-bold hover:underline">
            {t("auth.create")}
          </Link>
        </p>
      </div>
    </div>
  );
}
